import FunnelChartColors from '@/Enums/Attribution/FunnelChartColors.enum';
import OfflineSalesTitles from '@/Enums/Attribution/OfflineSalesTitles.enum';
import Color from '@/Enums/Color.enum';
import { scaleFunnelValues } from '@/helpers/attribution/chartData';
import { numberToUsFormat } from '@/helpers/valueFormatter/numberFormatter.helpers';
import { useAppSelector } from '@/store/hooks';
import { selectOfflineSalesData } from '@/store/selectors/attribution/offlineSales.selectors';
import { Box, CircularProgress } from '@mui/material';
import { ChartData, Chart as ChartJS, ChartOptions, registerables } from 'chart.js';
import { FunnelController, TrapezoidElement } from 'chartjs-chart-funnel';
import { useMemo } from 'react';
import { Chart } from 'react-chartjs-2';

ChartJS.register(...registerables, FunnelController, TrapezoidElement);

const options: ChartOptions<'funnel'> = {
  indexAxis: 'x',
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },
    datalabels: {
      display: false,
    },
  },
};

const SalesFunnelCard = () => {
  const { data, loading } = useAppSelector(selectOfflineSalesData);

  const dataValues = useMemo(
    () => [data.impressions, data.reach, data.totalSales, data.matchedSales, data.extrapolatedConversions],
    [data],
  );

  const funnelDataValues = useMemo(() => scaleFunnelValues(dataValues), [dataValues]);

  const chartData = useMemo<ChartData<'funnel'>>(
    () => ({
      labels: [
        OfflineSalesTitles.Impressions,
        OfflineSalesTitles.Reach,
        OfflineSalesTitles.TotalSales,
        OfflineSalesTitles.MatchedSales,
        OfflineSalesTitles.ExtrapolatedConversions,
      ],
      datasets: [
        {
          label: 'Sales Funnel',
          data: funnelDataValues,
          backgroundColor: [
            FunnelChartColors.Impressions,
            FunnelChartColors.Reach,
            FunnelChartColors.TotalSales,
            FunnelChartColors.MatchedSales,
            FunnelChartColors.ExtrapolatedConversions,
          ],
          hoverBackgroundColor: '#FDCB58',
          borderWidth: 1,
          borderColor: '#FFFFFF',
        },
      ],
    }),
    [funnelDataValues],
  );

  return (
    <div className='w-full print:p-6'>
      <div className='flex flex-col gap-2 w-full mb-[24px]'>
        <div className='text-lg text-[#2E2C34] mb-[20px]'>Sales Funnel</div>
        <div className='flex justify-between'>
          {!loading &&
            chartData.labels!.map((label, index) => (
              <Box
                key={index}
                sx={{
                  textAlign: 'start',
                  width: '20%',
                }}
              >
                <div className={`text-sm font-semibold text-[${Color.Grey}]`}>{label as string}</div>
                <div className='text-3xl font-bold'>
                  {numberToUsFormat(dataValues[index], { maximumFractionDigits: 0 })}
                </div>
              </Box>
            ))}
        </div>
      </div>
      <div className='flex flex-col w-full'>
        {loading ? (
          <div className='flex w-full justify-center mt-8 mb-16'>
            <CircularProgress />
          </div>
        ) : (
          <div className='funnel_chart'>
            <Chart type='funnel' data={chartData} options={options} />
          </div>
        )}
      </div>
    </div>
  );
};
export default SalesFunnelCard;
